import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import Placeholder from '@tiptap/extension-placeholder';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import { clean } from './HtmlDocumentView';

// Edits one DRAFT version of a QMS document: the full version and the overview,
// side by side as two tabs on the same draft. Changes autosave to the content
// route (which sanitises and stamps who saved), so closing the editor loses
// nothing and the draft can be picked up again from QMS Control.
//
// The toolbar is deliberately small. There are no fonts, colours or alignment
// because every stored body gets the one house rendering (.qms-doc); a policy
// written this year and one written in three years look the same.

const SAVE_DELAY_MS = 1500;

const FULL_PLACEHOLDER = 'The full version: the controlled wording of the policy. Use headings for each section (Purpose, Scope, Eligibility, …), lists for steps and conditions, and a table where the policy has one.';
const OVERVIEW_PLACEHOLDER = 'The overview: the plain-English summary staff read first. What the policy is for, who it applies to, what people need to do, and any figures or entitlements (amounts, periods, deadlines). No more than a screen of text.';

function buildExtensions(placeholder) {
  return [
    // Tiptap 3's StarterKit also bundles Link, Underline, ListKeymap and
    // TrailingNode. Link is configured on its own below; the other three are
    // off so the editor behaves, and emits HTML, as it did on Tiptap 2.
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] },
      code: false,
      codeBlock: false,
      link: false,
      underline: false,
      listKeymap: false,
      trailingNode: false,
    }),
    Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true, protocols: ['https', 'mailto'], HTMLAttributes: { rel: null, target: null } }),
    Image.configure({ inline: false, allowBase64: false }),
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    Placeholder.configure({ placeholder }),
  ];
}

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`px-2 py-1 text-[11px] rounded border transition-colors disabled:opacity-40 ${active ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:border-navy hover:text-navy'}`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor, onImage }) {
  // Re-render on selection so the active states follow the caret.
  const [, force] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const tick = () => force((n) => n + 1);
    editor.on('selectionUpdate', tick);
    editor.on('transaction', tick);
    return () => { editor.off('selectionUpdate', tick); editor.off('transaction', tick); };
  }, [editor]);
  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes('link').href || '';
    const url = window.prompt('Link address (https:// or mailto:)', previous);
    if (url === null) return;
    if (!url.trim()) { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    if (!/^(https:\/\/|mailto:)/i.test(url.trim())) { window.alert('Links in a policy must start with https:// or mailto:'); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  const inTable = editor.isActive('table');

  return (
    <div className="flex items-center gap-1 flex-wrap px-3 py-2 border-b border-gray-200 bg-gray-50">
      {[1, 2, 3].map((level) => (
        <ToolbarButton key={level} title={`Heading ${level}`} active={editor.isActive('heading', { level })} onClick={() => editor.chain().focus().toggleHeading({ level }).run()}>H{level}</ToolbarButton>
      ))}
      <ToolbarButton title="Paragraph" active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()}>¶</ToolbarButton>
      <span className="w-px h-5 bg-gray-200 mx-1" />
      <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></ToolbarButton>
      <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></ToolbarButton>
      <ToolbarButton title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></ToolbarButton>
      <span className="w-px h-5 bg-gray-200 mx-1" />
      <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</ToolbarButton>
      <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</ToolbarButton>
      <ToolbarButton title="Indent (nest in the list)" disabled={!editor.can().sinkListItem('listItem')} onClick={() => editor.chain().focus().sinkListItem('listItem').run()}>→</ToolbarButton>
      <ToolbarButton title="Outdent" disabled={!editor.can().liftListItem('listItem')} onClick={() => editor.chain().focus().liftListItem('listItem').run()}>←</ToolbarButton>
      <ToolbarButton title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</ToolbarButton>
      <ToolbarButton title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>―</ToolbarButton>
      <span className="w-px h-5 bg-gray-200 mx-1" />
      <ToolbarButton title="Link" active={editor.isActive('link')} onClick={setLink}>Link</ToolbarButton>
      <ToolbarButton title="Insert image" onClick={onImage}>Image</ToolbarButton>
      <ToolbarButton title="Insert table" active={inTable} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>Table</ToolbarButton>
      {inTable && (
        <>
          <ToolbarButton title="Add row below" onClick={() => editor.chain().focus().addRowAfter().run()}>+Row</ToolbarButton>
          <ToolbarButton title="Add column after" onClick={() => editor.chain().focus().addColumnAfter().run()}>+Col</ToolbarButton>
          <ToolbarButton title="Delete row" onClick={() => editor.chain().focus().deleteRow().run()}>−Row</ToolbarButton>
          <ToolbarButton title="Delete column" onClick={() => editor.chain().focus().deleteColumn().run()}>−Col</ToolbarButton>
          <ToolbarButton title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}>✕ Table</ToolbarButton>
        </>
      )}
      <span className="flex-1" />
      <ToolbarButton title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>Undo</ToolbarButton>
      <ToolbarButton title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>Redo</ToolbarButton>
    </div>
  );
}

export default function PolicyEditor({ doc, version, initialContent, onClose, onSubmitted }) {
  const { toast } = useToast();
  const [tab, setTab] = useState('full');
  const [versionNumber, setVersionNumber] = useState(version.version_number || '');
  const [changeSummary, setChangeSummary] = useState(version.change_summary || '');
  const [saveState, setSaveState] = useState({ status: 'idle', at: null, error: '' });
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = useRef({});
  const timer = useRef(null);
  const imageInput = useRef(null);
  const changed = useRef(false);

  const fullEditor = useEditor({
    extensions: buildExtensions(FULL_PLACEHOLDER),
    content: clean(initialContent?.html_content || ''),
    editorProps: { attributes: { class: 'qms-doc' } },
    onUpdate: ({ editor }) => queue({ html_content: editor.getHTML() }),
  });
  const overviewEditor = useEditor({
    extensions: buildExtensions(OVERVIEW_PLACEHOLDER),
    content: clean(initialContent?.summary_html || ''),
    editorProps: { attributes: { class: 'qms-doc qms-doc-overview' } },
    onUpdate: ({ editor }) => queue({ summary_html: editor.getHTML() }),
  });

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const fields = pending.current;
    pending.current = {};
    if (Object.keys(fields).length === 0) return true;
    setSaveState((s) => ({ ...s, status: 'saving', error: '' }));
    try {
      const saved = await api.put(`qms/documents/${doc.id}/versions/${version.id}/content`, fields);
      changed.current = true;
      setSaveState({ status: 'saved', at: saved.saved_at || new Date().toISOString(), error: '' });
      return true;
    } catch (e) {
      // Put the fields back so the next save retries them.
      pending.current = { ...fields, ...pending.current };
      setSaveState({ status: 'error', at: null, error: e.message || 'Save failed' });
      return false;
    }
  }, [doc.id, version.id]);

  const queue = useCallback((fields) => {
    pending.current = { ...pending.current, ...fields };
    setSaveState((s) => (s.status === 'saving' ? s : { ...s, status: 'dirty' }));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { flush(); }, SAVE_DELAY_MS);
  }, [flush]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') handleClose();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); flush(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = async () => {
    const ok = await flush();
    if (!ok && !window.confirm('The last change could not be saved. Close anyway and lose it?')) return;
    onClose(changed.current);
  };

  const activeEditor = tab === 'full' ? fullEditor : overviewEditor;

  const handleImagePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeEditor) return;
    if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type)) { toast('Only PNG, JPEG, GIF and WebP images can be placed in a document', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { toast('Image too large (max 5 MB)', 'error'); return; }
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await api.post(`qms/documents/${doc.id}/versions/${version.id}/assets`, { filename: file.name, content_type: file.type, data });
      activeEditor.chain().focus().setImage({ src: res.url, alt: file.name }).run();
    } catch (err) {
      toast(err.message || 'Image upload failed', 'error');
    }
  };

  const handleSuggest = async () => {
    if (!overviewEditor) return;
    const existing = overviewEditor.getText().trim();
    if (existing && !window.confirm('Replace the current overview with an AI suggestion? You can edit it afterwards.')) return;
    await flush();
    setSuggesting(true);
    try {
      const res = await api.post(`qms/documents/${doc.id}/versions/${version.id}/summary/suggest`, {});
      overviewEditor.commands.setContent(clean(res.suggestion_html), { emitUpdate: false });
      queue({ summary_html: overviewEditor.getHTML() });
      setSuggested(true);
      setTab('overview');
    } catch (e) {
      toast(e.message || 'Could not get a suggestion', 'error');
    } finally {
      setSuggesting(false);
    }
  };

  const handleSubmit = async () => {
    if (!fullEditor || !fullEditor.getText().trim()) { toast('The full version is empty. Write or import the content before submitting.', 'error'); return; }
    if (!versionNumber.trim()) { toast('Give this version a number first', 'error'); return; }
    if (!window.confirm(`Submit version ${versionNumber} for approval?\n\nSomeone other than you will need to approve it before it goes live.`)) return;
    setBusy(true);
    try {
      const ok = await flush();
      if (!ok) return;
      await api.put(`qms/documents/${doc.id}/versions/${version.id}/submit`, {});
      toast(`Version ${versionNumber} submitted for approval`, 'success');
      onSubmitted?.();
      onClose(true);
    } catch (e) {
      toast(e.message || 'Could not submit', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm(`Discard this draft (v${versionNumber || version.version_number})?\n\nEverything written in it is deleted. The live version is not affected.`)) return;
    setBusy(true);
    try {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      pending.current = {};
      await api.delete(`qms/documents/${doc.id}/versions/${version.id}`);
      toast('Draft discarded', 'success');
      onClose(true);
    } catch (e) {
      toast(e.message || 'Could not discard', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveLabel = saveState.status === 'saving' ? 'Saving…'
    : saveState.status === 'dirty' ? 'Unsaved changes'
    : saveState.status === 'error' ? `Not saved: ${saveState.error}`
    : saveState.status === 'saved' ? `Saved ${new Date(saveState.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
    : 'All changes save automatically';

  return createPortal(
    <div className="fixed inset-0 z-[210] bg-gray-100 flex flex-col">
      <input ref={imageInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={handleImagePicked} />

      {/* Header */}
      <div className="bg-navy text-white px-5 py-3 flex items-center gap-4 flex-shrink-0 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] text-white/60 font-mono">{doc.doc_number} · editing draft{version.based_on_version_id ? ' from the live version' : ''}</div>
          <div className="font-bold text-[14px] leading-tight truncate">{doc.title}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap ml-auto">
          <label className="flex items-center gap-1.5 text-[10px] text-white/70">
            Version
            <input
              value={versionNumber}
              onChange={(e) => { setVersionNumber(e.target.value); queue({ version_number: e.target.value }); }}
              className="w-16 bg-white/10 border border-white/20 rounded px-2 py-1 text-[11px] text-white font-mono focus:outline-none focus:border-gold"
              placeholder="1.1"
            />
          </label>
          <label className="flex items-center gap-1.5 text-[10px] text-white/70">
            What changed
            <input
              value={changeSummary}
              onChange={(e) => { setChangeSummary(e.target.value); queue({ change_summary: e.target.value }); }}
              className="w-56 bg-white/10 border border-white/20 rounded px-2 py-1 text-[11px] text-white focus:outline-none focus:border-gold"
              placeholder="Summary for the version history"
            />
          </label>
          <span className={`text-[10px] ${saveState.status === 'error' ? 'text-red-300' : 'text-white/60'}`}>{saveLabel}</span>
          <button onClick={handleClose} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Close (changes are saved)">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-5 flex items-center gap-1 flex-shrink-0" role="tablist" data-tour="qms-editor-tabs">
        {[['full', 'Full version'], ['overview', 'Overview']].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-[11px] font-semibold border-b-2 -mb-px transition-colors ${tab === key ? 'border-gold text-navy' : 'border-transparent text-gray-500 hover:text-navy'}`}
          >
            {label}
          </button>
        ))}
        <span className="text-[9px] text-gray-400 ml-3">
          {tab === 'full' ? 'The controlled wording. This is the version people acknowledge.' : 'The plain-English summary staff read first. Optional, but most policies want one.'}
        </span>
        {tab === 'overview' && (
          <button
            type="button"
            onClick={handleSuggest}
            disabled={suggesting}
            className="ml-auto my-1.5 text-[10px] font-semibold text-gold border border-gold rounded-lg px-2.5 py-1 hover:bg-gold hover:text-white transition-colors disabled:opacity-50"
            title="Draft an overview from the full version with AI. It is only a suggestion until you save it."
          >
            {suggesting ? 'Suggesting…' : 'Suggest a draft'}
          </button>
        )}
      </div>

      {/* Editor */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-4xl mx-auto my-5 bg-white border border-gray-200 rounded-xl overflow-hidden qms-editor">
          <div className={tab === 'full' ? '' : 'hidden'}>
            <Toolbar editor={fullEditor} onImage={() => imageInput.current?.click()} />
            <EditorContent editor={fullEditor} />
          </div>
          <div className={tab === 'overview' ? '' : 'hidden'}>
            <Toolbar editor={overviewEditor} onImage={() => imageInput.current?.click()} />
            {suggested && (
              <div className="mx-5 mt-3 text-[9px] bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 rounded-lg">
                This overview was suggested by AI from the full version. Read it as HR would read it, correct it, and it is yours once saved.
              </div>
            )}
            <EditorContent editor={overviewEditor} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-5 py-3 flex items-center gap-2 flex-shrink-0">
        <button onClick={handleDiscard} disabled={busy} className="text-[10px] text-red-600 hover:text-red-700 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
          Discard draft
        </button>
        <span className="flex-1" />
        <button onClick={handleClose} disabled={busy} className="px-3 py-1.5 border border-gray-200 text-[11px] text-gray-600 rounded-lg hover:border-gray-300 transition-colors">
          Close
        </button>
        <button onClick={handleSubmit} disabled={busy} className="px-3 py-1.5 bg-navy text-white text-[11px] font-semibold rounded-lg hover:bg-navy/80 disabled:opacity-50 transition-colors">
          {busy ? 'Working…' : 'Submit for approval'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
